/**
 * The ONE minimum-payment formula.
 *
 * Previously this existed three times with subtly different behavior:
 *   - calculations.ts (correct)
 *   - CreditCardPayoff.tsx  (capped at the PRE-interest balance)
 *   - CreditCardManager.tsx (capped at the PRE-interest balance)
 * ...which is why the same card showed different minimums in different views.
 *
 * The interest model is also unified here: monthly compounding at APR/12,
 * accrued once per statement cycle. The old private helper used APR/365 × a
 * hardcoded 30 days, and a separate path added a partial-month stub that could
 * go negative.
 */

export interface CardTerms {
  interestRate: number; // APR as a percent, e.g. 24.99
  minimumPayment: number; // dollar floor
  minPaymentPrincipalPercent?: number; // principal component, typically 1
  dueDay?: number | null;
}

/** Monthly periodic rate. The single interest model for the whole engine. */
export function monthlyRate(apr: number): number {
  return apr / 100 / 12;
}

/** Interest accrued over one statement cycle on `balance`. */
export function cycleInterest(balance: number, apr: number): number {
  if (balance <= 0) return 0;
  return balance * monthlyRate(apr);
}

/**
 * The issuer minimum for one cycle.
 *
 *   interest      = balance × APR/12
 *   principalPart = balance × principalPct/100      (PRE-interest balance)
 *   minimum       = max(floor, interest + principalPart)
 *   payment       = min(minimum, balance + interest) (POST-interest cap)
 *
 * The post-interest cap matters on the final cycle: capping at the pre-interest
 * balance leaves the last month's interest unpaid and the card never closes.
 */
export function computeMinimumPayment(
  balance: number,
  apr: number,
  floor: number,
  principalPct: number = 1
): number {
  if (balance <= 0) return 0;
  const interest = cycleInterest(balance, apr);
  const principalPart = balance * (principalPct / 100);
  const raw = Math.max(floor, interest + principalPart);
  return Math.min(raw, balance + interest);
}

/** Convenience wrapper taking a card-shaped object. */
export function minimumPaymentForCard(card: CardTerms, balance: number): number {
  return computeMinimumPayment(
    balance,
    card.interestRate,
    card.minimumPayment,
    card.minPaymentPrincipalPercent ?? 1
  );
}

/**
 * True when a payment can never close the card because it does not cover the
 * interest accruing each cycle. Callers surface this as a warning — activation
 * is still permitted.
 */
export function isPaymentFeasible(balance: number, apr: number, payment: number): boolean {
  if (balance <= 0) return true;
  return payment > cycleInterest(balance, apr) + 0.005;
}

/** The smallest payment that makes any progress at all, for error messaging. */
export function minimumViablePayment(balance: number, apr: number): number {
  return Math.ceil((cycleInterest(balance, apr) + 0.01) * 100) / 100;
}
